import { useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { GitCompare } from "lucide-react";
import { BRANDS, brandById } from "@/lib/brands";
import { addDays, formatCents, formatDateTime, formatInt, formatPercent, mountainDay } from "@/lib/format";
import type { BrandTotals, ReportSummary } from "@/lib/types";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { KIND_GROUPS, KIND_LABEL, useByBrand, useReports } from "./reportHooks";
import { LabelChip } from "./EmailLabel";

const RANGES: { label: string; days: number | "year" }[] = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "This year", days: "year" },
];

/** Every broadcast and automation email with its results; totals by brand and segment for the range. */
export function ReportsListPage() {
  const search = useSearch({ from: "/email/reports" });
  const navigate = useNavigate({ from: "/email/reports" });
  const today = mountainDay();
  const since = search.since ?? addDays(today, -29);
  const until = search.until; // exclusive day after the range when set
  const set = (patch: Partial<typeof search>) => navigate({ search: { ...search, ...patch }, replace: true });
  const reports = useReports({ brand: search.brand, kind: search.kind, since, until, segment: search.segment });
  const byBrand = useByBrand(since, until, search.kind?.startsWith("group:") ? undefined : search.kind);
  const rows = useMemo(() => reports.data?.pages.flatMap((p) => p.reports) ?? [], [reports.data]);
  const [picked, setPicked] = useState<number[]>([]);
  const toggle = (id: number) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= 10 ? p : [...p, id]));
  const rangeKey = (r: (typeof RANGES)[number]) => (r.days === "year" ? `${today.slice(0, 4)}-01-01` : addDays(today, -(r.days - 1)));

  return (
    <div className="space-y-3">
      <PageHeader
        title="Email reports"
        sub="every broadcast and automation email, newest first"
        actions={
          picked.length >= 2 ? (
            <Button size="sm" asChild>
              <Link to="/email/reports/compare" search={{ ids: picked }}>
                <GitCompare /> Compare {picked.length}
              </Link>
            </Button>
          ) : (
            <Button size="sm" variant="outline" disabled title="Tick 2 to 10 emails below">
              <GitCompare /> Compare (tick 2 to 10)
            </Button>
          )
        }
      />
      <div className="flex flex-wrap items-end gap-2">
        <div role="radiogroup" aria-label="Date range" className="flex gap-1">
          {RANGES.map((r) => {
            const s = rangeKey(r);
            const on = since === s && !until;
            return (
              <Button key={r.label} size="sm" role="radio" aria-checked={on} variant={on ? "default" : "outline"} onClick={() => set({ since: s, until: undefined })}>
                {r.label}
              </Button>
            );
          })}
        </div>
        <div className="space-y-0.5">
          <Label htmlFor="rep-since">From</Label>
          <Input id="rep-since" type="date" className="h-8 w-36" value={since} onChange={(e) => set({ since: e.target.value || undefined })} />
        </div>
        <div className="space-y-0.5">
          <Label htmlFor="rep-until">Through</Label>
          <Input
            id="rep-until"
            type="date"
            className="h-8 w-36"
            value={until ? addDays(until, -1) : ""}
            onChange={(e) => set({ until: e.target.value ? addDays(e.target.value, 1) : undefined })}
          />
        </div>
        <Select className="w-auto" aria-label="Brand" value={search.brand ?? ""} onChange={(e) => set({ brand: e.target.value ? Number(e.target.value) : undefined })}>
          <option value="">All brands</option>
          {BRANDS.map((b) => (
            <option key={b.id} value={b.id}>
              {b.short}: {b.name}
            </option>
          ))}
        </Select>
        <Select className="w-auto" aria-label="Kind of email" value={search.kind ?? ""} onChange={(e) => set({ kind: e.target.value || undefined })}>
          <option value="">Every kind</option>
          {Object.entries(KIND_GROUPS).map(([k, v]) => (
            <option key={k} value={`group:${k}`}>
              {v}
            </option>
          ))}
          {Object.entries(KIND_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
        {search.segment && (
          <Button size="sm" variant="ghost" onClick={() => set({ segment: undefined })}>
            Segment #{search.segment} ×
          </Button>
        )}
      </div>

      <BrandTotalsCard rows={byBrand.data?.brands ?? []} segments={byBrand.data?.segments ?? []} loading={byBrand.isLoading} onSegment={(id) => set({ segment: id })} />

      <Card className="overflow-x-auto">
        {reports.isLoading && <Skeleton className="m-3 h-64" />}
        {!reports.isLoading && rows.length === 0 && <Empty title="No emails were sent in this range.">Pick a longer range or another brand.</Empty>}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH className="w-8">
                  <span className="sr-only">Compare</span>
                </TH>
                <TH>Sent (MT)</TH>
                <TH>Email</TH>
                <TH>Brand</TH>
                <TH className="text-right">Sent</TH>
                <TH className="text-right" title="Delivered as a share of sent">
                  Delivered
                </TH>
                <TH className="text-right" title="Opened by a person (machine opens such as Apple Mail Privacy Protection left out)">
                  Opened
                </TH>
                <TH className="text-right">Clicked</TH>
                <TH className="text-right" title="Clicks as a share of people who opened">
                  Click-to-open
                </TH>
                <TH className="text-right">Unsubscribed</TH>
                <TH className="text-right">Bounced</TH>
                <TH className="text-right">Revenue</TH>
              </tr>
            </THead>
            <tbody className={cn(reports.isFetching && !reports.isFetchingNextPage && "opacity-60")}>
              {rows.map((r) => (
                <ReportRow key={r.report_unit_id} r={r} picked={picked.includes(r.report_unit_id)} onPick={() => toggle(r.report_unit_id)} />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      {reports.hasNextPage && (
        <Button size="sm" variant="outline" onClick={() => reports.fetchNextPage()} disabled={reports.isFetchingNextPage}>
          Show 100 more
        </Button>
      )}
    </div>
  );
}

function ReportRow({ r, picked, onPick }: { r: ReportSummary; picked: boolean; onPick: () => void }) {
  return (
    <TR className={cn(picked && "bg-[var(--color-row-selected)]")}>
      <TD>
        <Checkbox aria-label={`Compare ${r.name}`} checked={picked} onChange={onPick} />
      </TD>
      <TD className="num whitespace-nowrap">{formatDateTime(r.first_sent_at)}</TD>
      <TD className="max-w-[24rem]">
        <Link to="/email/reports/$reportId" params={{ reportId: String(r.report_unit_id) }} className="block truncate font-medium text-[var(--color-primary)] hover:underline">
          {r.name}
        </Link>
        <div className="truncate text-xs text-[var(--color-muted-foreground)]">
          {r.test && (
            <Badge variant="warning" className="mr-1.5" title="Sent only to test contacts: not counted anywhere">
              test
            </Badge>
          )}
          <LabelChip label={r.label} className="mr-1.5" />
          {KIND_LABEL[r.kind] ?? r.kind}
          {r.segment_name ? ` to ${r.segment_name}` : ""}
          {r.step_key ? `, step ${r.step_key}` : ""}
        </div>
      </TD>
      <TD>{r.brand_id ? <BrandMark id={r.brand_id} /> : "—"}</TD>
      <TD className="num text-right">{formatInt(r.sent)}</TD>
      <TD className="num text-right">{r.delivered ? formatPercent(r.delivered / Math.max(r.sent, 1)) : "—"}</TD>
      <TD className="num text-right" title={`${formatInt(r.human_opens_unique)} people; ${formatInt(r.opens_unique)} with machine opens (${formatPercent(r.open_rate)})`}>
        {formatPercent(r.human_open_rate)}
      </TD>
      <TD className="num text-right" title={`${formatInt(r.clicks_unique)} people, ${formatInt(r.clicks_total)} clicks`}>
        {formatPercent(r.click_rate)}
      </TD>
      <TD className="num text-right">{formatPercent(r.click_to_open_rate)}</TD>
      <TD className="num text-right">{r.unsubscribes ? formatInt(r.unsubscribes) : "—"}</TD>
      <TD className="num text-right" title={`${r.bounces_hard} hard, ${r.bounces_soft} soft`}>
        {r.bounces_hard + r.bounces_soft ? formatInt(r.bounces_hard + r.bounces_soft) : "—"}
      </TD>
      <TD className="num text-right">{r.revenue_cents ? formatCents(r.revenue_cents) : "—"}</TD>
    </TR>
  );
}

/** Totals for the range by brand (and, under each, by segment). */
function BrandTotalsCard({ rows, segments, loading, onSegment }: { rows: BrandTotals[]; segments: import("@/lib/types").SegmentTotals[]; loading: boolean; onSegment: (id: number) => void }) {
  const [open, setOpen] = useState(false);
  if (loading) return <Skeleton className="h-28" />;
  if (rows.length === 0) return null;
  const all = rows.reduce(
    (a, r) => ({ ...a, units: a.units + r.units, sent: a.sent + r.sent, delivered: a.delivered + r.delivered, human_opens_unique: a.human_opens_unique + r.human_opens_unique, clicks_unique: a.clicks_unique + r.clicks_unique, unsubscribes: a.unsubscribes + r.unsubscribes, bounces: a.bounces + r.bounces, complaints: a.complaints + r.complaints, orders: a.orders + r.orders, revenue_cents: a.revenue_cents + r.revenue_cents }),
    { brand_id: null, units: 0, sent: 0, delivered: 0, opens_unique: 0, human_opens_unique: 0, clicks_unique: 0, clicks_total: 0, unsubscribes: 0, bounces: 0, complaints: 0, orders: 0, revenue_cents: 0 } as BrandTotals,
  );
  const line = (r: BrandTotals, label: React.ReactNode, strong = false) => {
    const b = Math.max(r.delivered || r.sent, 1);
    return (
      <TR key={String(r.brand_id ?? "all")} className={cn(strong && "font-semibold")}>
        <TD>{label}</TD>
        <TD className="num text-right">{formatInt(r.units)}</TD>
        <TD className="num text-right">{formatInt(r.sent)}</TD>
        <TD className="num text-right">{formatPercent(r.human_opens_unique / b)}</TD>
        <TD className="num text-right">{formatPercent(r.clicks_unique / b)}</TD>
        <TD className="num text-right">{formatInt(r.unsubscribes)}</TD>
        <TD className="num text-right">{formatInt(r.bounces)}</TD>
        <TD className="num text-right">{formatInt(r.complaints)}</TD>
        <TD className="num text-right">{formatInt(r.orders)}</TD>
        <TD className="num text-right">{formatCents(r.revenue_cents)}</TD>
      </TR>
    );
  };
  return (
    <Card className="overflow-x-auto">
      <CardHeader>
        <CardTitle>Totals for this range</CardTitle>
        {segments.length > 0 && (
          <button className="text-xs text-[var(--color-primary)] hover:underline" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            {open ? "Hide segments" : `By segment (${segments.length})`}
          </button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <THead>
            <tr>
              <TH>Brand</TH>
              <TH className="text-right">Emails</TH>
              <TH className="text-right">Sent</TH>
              <TH className="text-right">Opened (people)</TH>
              <TH className="text-right">Clicked</TH>
              <TH className="text-right">Unsubscribed</TH>
              <TH className="text-right">Bounced</TH>
              <TH className="text-right">Spam reports</TH>
              <TH className="text-right">Orders</TH>
              <TH className="text-right">Revenue</TH>
            </tr>
          </THead>
          <tbody>
            {rows.map((r) => line(r, r.brand_id ? <span className="flex items-center gap-2"><BrandMark id={r.brand_id} /><span className="text-xs text-[var(--color-muted-foreground)]">{brandById(r.brand_id)?.name}</span></span> : "No brand"))}
            {rows.length > 1 && line(all, "All brands", true)}
          </tbody>
        </Table>
        {open && (
          <div className="border-t">
            <Table>
              <THead>
                <tr>
                  <TH>Segment</TH>
                  <TH>Brand</TH>
                  <TH className="text-right">Emails</TH>
                  <TH className="text-right">Sent</TH>
                  <TH className="text-right">Opened (any)</TH>
                  <TH className="text-right">Clicked</TH>
                  <TH className="text-right">Unsubscribed</TH>
                  <TH className="text-right">Orders</TH>
                  <TH className="text-right">Revenue</TH>
                </tr>
              </THead>
              <tbody>
                {segments.map((s) => {
                  const b = Math.max(s.delivered || s.sent, 1);
                  return (
                    <TR key={`${s.brand_id}-${s.segment_id}`}>
                      <TD>
                        <button className="text-[var(--color-primary)] hover:underline" onClick={() => s.segment_id && onSegment(s.segment_id)}>
                          {s.segment_name || `Segment #${s.segment_id}`}
                        </button>
                      </TD>
                      <TD>{s.brand_id ? <BrandMark id={s.brand_id} /> : "—"}</TD>
                      <TD className="num text-right">{formatInt(s.units)}</TD>
                      <TD className="num text-right">{formatInt(s.sent)}</TD>
                      <TD className="num text-right">{formatPercent(s.opens_unique / b)}</TD>
                      <TD className="num text-right">{formatPercent(s.clicks_unique / b)}</TD>
                      <TD className="num text-right">{formatInt(s.unsubscribes)}</TD>
                      <TD className="num text-right">{formatInt(s.orders)}</TD>
                      <TD className="num text-right">{formatCents(s.revenue_cents)}</TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
